// initial state
const state = () => ({
    scenes: [],
    scene: {
        settings: {}
    },

    ongoingScenes: [],
})

// getters
const getters = {
    currentScene: (state) => (id) => {
        if (id) {
            let scene = state.scenes.find(scene => scene.id === id);
            if (scene) {
                return scene;
            }

            return {
                settings: {}
            };
        }

        return state.scene;
    },

    getSceneTrack: (state) => (sceneId, trackId) => {
        let scene = state.scenes.find(scene => scene.id === sceneId);
        if (scene) {
            return scene.tracks.find(track => track.id === trackId);
        }

        return null;
    }
}

// actions
const actions = {
    getScenes: async ({ commit }) => {
        let { data } = await axios.get('scene');

        commit('setScenes', data);
    },

    createScene: async ({ commit }, payload) => {
        return await axios.post('scene', payload).then(({ data }) => {
            commit('addScene', data);
            return data;
        }).catch(({ response }) => {
            // console.log(response.data);
            throw response.data;
        });
    },

    updateScene: async ({ commit }, payload) => {
        // console.log('updateScene action', payload);
        return await axios.put('scene/' + payload.id, payload).then(({ data }) => {
            commit('updateScene', data);
            return data;
        }).catch(({ response }) => {
            // console.log(response.data);
            throw response.data;
        });
    },

    // Update position indexes
    updateScenePositions: async ({ commit }, payload) => {
        // let position = 0;
        let positionData = JSON.parse(JSON.stringify(payload));
        // positionData.forEach(scene => scene.position = position++);

        return await axios.put('scene/positions', positionData).then(() => {
            commit('setScenes', positionData);
        }).catch(() => {
            throw 'Error updating scene positions';
        });
    },

    deleteScene: async ({ commit }, payload) => {
        return await axios.delete('scene/' + payload.id).then(() => {
            commit('removeScene', payload.id);
        }).catch(() => {
            throw 'Error deleting scene';
        });
    },

    addTracksToScene: async ({ commit }, payload) => {
        // Only send track id's
        let trackIds = payload.tracks.slice().map(track => track.id);

        return await axios.post('scene/' + payload.scene.id + '/tracks', { tracks: trackIds }).then((response) => {
            commit('addTrackToScene', payload);
            return response.data;
        }).catch(() => {
            throw 'Error adding tracks to scene';
        });
    },

    updateTrackPositions: async ({ commit }, payload) => {
        await axios.post('scene/' + payload.scene.id + '/tracks/position', { tracks: payload.scene.tracks }).then(() => {
            commit('updateSceneData', { scene: payload.scene, key: 'tracks', value: payload.scene.tracks });
        }).catch(() => {
            throw 'Error updating track positions';
        });
    },

    updateSceneTrack: async ({ commit }, payload) => {
        return await axios.put('scene/' + payload.scene_id + '/track/' + payload.track.id, payload.track).catch(() => {
            throw 'Error updating track';
        });
    },

    removeTrackFromScene: async ({ commit }, payload) => {
        let trackIds = payload.tracks.slice().map(track => track.id);
        return await axios.post('scene/' + payload.scene.id + '/tracks/remove', { tracks: trackIds }).then(() => {
            commit('removeTrackFromScene', payload);
        }).catch(() => {
            throw 'Error removing track from scene';
        });
    },

    toggleActiveTrack: async ({ commit }, payload) => {
        return await axios.post('scene/' + payload.scene.id + '/track/' + payload.track.id + '/toggle').then((response) => {
            commit('toggleSceneTrackStatus', { scene_id: payload.scene.id, track: response.data });
        }).catch(() => {
            console.error('Error toggling track');
        });
    }
}

// mutations
const mutations = {
    setScenes(state, data) {
        state.scenes = data;
    },

    setScene(state, data) {
        state.scene = data;
    },

    addScene(state, data) {
        data.tracks = [];
        state.scenes.push(data);
    },

    updateSceneData(state, data) {
        data.scene[data.key] = data.value;
    },

    updateSceneState(state, data) {
        let scene = state.scenes.find(scene => scene.id === data.id);

        if (scene) {
            scene.state = data.value;
        }
    },

    updateScene(state, data) {
        let sceneIndex = state.scenes.findIndex(scene => scene.id === data.id);

        if (sceneIndex >= 0) {
            state.scenes[sceneIndex] = Object.assign(state.scenes[sceneIndex], data);
        }
    },

    updateSceneSettings(state, data) {
        if (data.scene.settings.length === 0) {
            data.scene.settings = {};
        }

        data.scene.settings[data.key] = data.value;
    },

    removeScene(state, data) {
        let index = state.scenes.findIndex(item => item.id === data);

        if (index >= 0) {
            state.scenes.splice(index, 1);
        }
    },

    addTrackToScene(state, data) {
        data.tracks.forEach(track => {
            console.log('addTrackToScene', track);
            if (!data.scene.tracks) {
                data.scene.tracks = [];
            }

            // Check if track already exists, we don't want to add it again
            let trackIndex = data.scene.tracks.findIndex(sceneTrack => sceneTrack.id === track.id);
            if (trackIndex >= 0) {
                return;
            }

            // Set default pivot values for track
            if (!track.pivot) {
                track.pivot = {
                    current_time: 0,
                    position: 0,
                    volume: track.volume ?? data.scene.user?.data?.default_values?.track_volume ?? 50,
                    loop: 0,
                    track_id: track.id,
                    status: 1,
                    scene_id: data.scene.id || null
                };
            }

            data.scene.tracks.push(track);

            if (!track.scenes) {
                track.scenes = [];
            }

            track.scenes.push({
                id: data.scene.id,
                type: data.scene.type,
                user_id: data.scene.user_id,
                soundboard_id: data.scene.soundboard_id,
                name: data.scene.name,
                background: data.scene.background,
                position: data.scene.position,
                settings: data.scene.settings,
                created_at: data.scene.created_at,
                updated_at: data.scene.updated_at,
                pivot: data.scene.pivot
            });

            let scene = state.scenes.find(scene => scene.id === data.scene.id);
            if (scene) {
                // If the track already exists in the scene, we don't want to add it again
                let existingTrackIndex = scene.tracks.findIndex(t => t.id === track.id);
                if (existingTrackIndex < 0) {
                    scene.tracks.push(JSON.parse(JSON.stringify(track)));
                }
            }
        });
    },

    updateSceneTrackPositionData(state, data) {
        // Update each track pivot.position value
        data.scene.tracks.splice(data.newIndex, 0, data.scene.tracks.splice(data.oldIndex, 1)[0]);

        // First we update the tracks array to have running pivot.position values
        data.scene.tracks.forEach((track, index) => {
            track.pivot.position = index;
        });
    },

    updateSceneTrackData(state, data) {
        console.log('updateSceneTrackData', data);
        let scene = state.scenes.find(scene => scene.id === data.scene_id);

        if (scene) {
            console.log('scene', scene);
            let track = scene.tracks.find(track => track.id === data.track.id);

            if (track) {
                console.log('track', track);

                track = data.track;

                console.log('track', track);
            }
        }
    },

    removeTrackFromScene(state, data) {
        data.tracks.forEach(track => {
            let scene = state.scenes.find(scene => scene.id === data.scene.id);
            let trackIndex = scene.tracks.findIndex(data => data.id === track.id);

            // Remove scene from track
            if (scene && scene.tracks[trackIndex]?.scenes) {
                let trackSceneIndex = scene.tracks[trackIndex].scenes.findIndex(item => item.id === scene.id);
                if (trackSceneIndex >= 0) {
                    scene.tracks[trackIndex].scenes.splice(trackSceneIndex, 1);
                }
            }

            // Remove track from scene
            if (trackIndex >= 0) {
                scene.tracks.splice(trackIndex, 1);
            }
        });
    },

    // Add scene id to ongoing scenes, if it doesn't exist
    attachOngoingScene(state, sceneId) {
        let sceneIndex = state.ongoingScenes.findIndex(id => id === sceneId);

        if (sceneIndex < 0) {
            state.ongoingScenes.push(sceneId);
        }
    },

    // Remove scene id from ongoing scenes
    detachOngoingScene(state, { sceneId, wipe = false }) {
        console.log('detachOngoingScene', sceneId, wipe);
        // If wipe, remove all ongoing scenes except the one passed in
        if (wipe) {
            // Create array with only the scene id passed in
            state.ongoingScenes = [sceneId];
        } else {
            let sceneIndex = state.ongoingScenes.findIndex(scene => scene === sceneId);

            if (sceneIndex >= 0) {
                state.ongoingScenes.splice(sceneIndex, 1);
            }
        }
    },

    toggleSceneTrackStatus(state, data) {
        let scene = state.scenes.find(scene => scene.id === data.scene_id);

        if (scene) {
            let track = scene.tracks.find(track => track.id === data.track.id);

            if (track) {
                track.pivot.status = !track.pivot.status;
            }
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
